import { FC } from 'react';

import { isEmpty } from 'lodash/fp';

import { sharedTestSelectors } from 'tests/shared/shared.selectors';

import { PathwayQuestionTypes } from 'models/PathwayTemplates';

import { StyledTagInput } from 'views/Widgets/StyledSelect';

import {
  LightweightPathwayQuestionAction,
  LightweightPathwayQuestionAnswer,
  LightweightPathwayQuestionBaseProps,
  TextArrayQuestionActionMeta,
  TextArrayValue
} from 'components/LightweightPathway/LightweightPathway.types';

interface Props
  extends LightweightPathwayQuestionBaseProps<TextArrayValue, TextArrayQuestionActionMeta> {}

type Value = Omit<TextArrayValue[number], 'title'> & {
  title?: string;
  label?: string;
};

export const TextArrayQuestion: FC<Props> = ({
  question,
  pathway,
  index,
  answer,
  handleAnswerChanged,
  parentQuestionId,
  pathwayIndex
}) => {
  const onChange = (value: Value[]) => {
    // normalize incoming tag items so chips always have a visible title
    const normalizedValue: TextArrayValue = (value || []).map((item: Value) => {
      const label: string = item?.title ?? item?.label ?? item?.value ?? '';
      return {
        id: item?.id ?? label,
        title: label,
        value: item?.value ?? label
      };
    });
    let newAnswer = { ...(answer || {}) } as LightweightPathwayQuestionAnswer<TextArrayValue>;

    let actionMeta: TextArrayQuestionActionMeta = {
      action: LightweightPathwayQuestionAction.AddFirst,
      questionId: question.id,
      type: PathwayQuestionTypes.TEXT_ARRAY
    };

    if (!isEmpty(newAnswer)) {
      newAnswer.value = normalizedValue;
      actionMeta = {
        ...actionMeta,
        action:
          normalizedValue.length > (answer?.value?.length || 0)
            ? LightweightPathwayQuestionAction.AddNewOption
            : normalizedValue.length === 0
            ? LightweightPathwayQuestionAction.RemoveLast
            : LightweightPathwayQuestionAction.RemoveOption
      };
    } else {
      newAnswer = {
        ...question,
        pathway: { ...pathway, index: pathwayIndex },
        parentQuestionId,
        index,
        value: normalizedValue
      };
    }

    handleAnswerChanged(newAnswer, actionMeta);
  };

  const value = answer?.value || [];

  const getOptionLabel = (option: TextArrayValue[number]) => {
    return option?.title || option?.value;
  };

  const formatOptionLabel = (option: TextArrayValue[number]) => {
    return option?.title || option?.value;
  };

  return (
    <StyledTagInput
      placeholder="Type response here..."
      onChange={onChange}
      value={value}
      getOptionLabel={getOptionLabel as (option: any) => string}
      formatOptionLabel={formatOptionLabel}
      testHook={sharedTestSelectors.lightweightPathwayTextQuestionInput(question.id)}
    />
  );
};
